import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AuthApi } from './auth.api';
import * as AuthActions from './auth.actions';
import { SnackbarService } from '../../core/services/snackbar.service';
import { AuthStoreService } from '../../core/services';
import { UserClearState } from '../user/user.actions';
@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(AuthApi);
  private readonly snackbar = inject(SnackbarService);
  private readonly authStoreService = inject(AuthStoreService);

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.RegisterRequest),
      switchMap(({ payload }) =>
        this.api.register(payload).pipe(
          map(() => AuthActions.RegisterSuccess()),
          catchError(error => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
            }
            return of(AuthActions.RegisterFailure({ error: error || 'An error occurred while registering company' }));

          })
        )
      )
    )
  );

  registerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.RegisterSuccess),
      map(() => {
        this.snackbar.showNotification('success', '', 'Registration successful! Please check your phone for OTP.', 'Close');
      })
    ),
    { dispatch: false }
  );

  registerFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.RegisterFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while registering company', 'Close');
      })
    ),
    { dispatch: false }
  ); 
  
  verifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.VerifyOtpRequest),
      switchMap(({ payload }) =>
        this.api.verifyOtp(payload).pipe(
          map(() => AuthActions.VerifyOtpSuccess()),
          catchError(error => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
              
            }
            return of(AuthActions.VerifyOtpFailure({ error: error || 'An error occurred while verifying OTP' }));
          })
        )
      )
    )
  );

  verifyOtpSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.VerifyOtpSuccess),
      map(() => {
        this.snackbar.showNotification('success', '', 'OTP verified successfully! Please login to continue.', 'Close');
      })
    ),
    { dispatch: false }
  );

  verifyOtpFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.VerifyOtpFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while verifying OTP', 'Close');
      })
    ),
    { dispatch: false }
  );

  resendOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ResendOtpRequest),
      switchMap(({ payload }) =>
        this.api.resendOtp(payload).pipe(
          map(() => AuthActions.ResendOtpSuccess()),
          catchError(error => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
            }
            return of(AuthActions.ResendOtpFailure({ error: error || 'An error occurred while resending OTP' }));
          })
        )
      )
    )
  );

  resendOtpSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ResendOtpSuccess),
      map(() => {
        this.snackbar.showNotification('success', '', 'OTP resent successfully! Please check your phone.', 'Close');
      })
    ),
    { dispatch: false }
  );

  resendOtpFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ResendOtpFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while resending OTP', 'Close');
      })
    ),
    { dispatch: false }
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LoginRequest),
      switchMap(({ payload }) =>
        this.api.login(payload).pipe(
          map((response) => AuthActions.LoginSuccess({ token: response.data.access_token })),
          catchError(error => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
            }
            return of(AuthActions.LoginFailure({ error: error || 'An error occurred while logging in' }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LoginSuccess, AuthActions.SsoExchangeSuccess),
      map(({ token }) => {
        this.authStoreService.setToken(token);
      })
    ),
    { dispatch: false }
  );

  clearUserStateOnSessionChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LoginSuccess, AuthActions.SsoExchangeSuccess, AuthActions.LogoutSuccess),
      map(() => UserClearState())
    )
  );

  ssoExchange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.SsoExchangeRequest),
      switchMap(({ payload }) =>
        this.api.orgExchange(payload).pipe(
          map((response) => AuthActions.SsoExchangeSuccess({ token: response.data.access_token })),
          catchError(error => of(AuthActions.SsoExchangeFailure({ error: error?.error?.message || error?.message || 'An error occurred while completing SSO login' })))
        )
      )
    )
  );

  ssoExchangeFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.SsoExchangeFailure),
      tap(() => {
        this.authStoreService.clear();
      })
    ),
    { dispatch: false }
  );

  loginFailure$ = createEffect(() => 
    this.actions$.pipe(
      ofType(AuthActions.LoginFailure),
      map(({ error }) => {
        // this.snackbar.showNotification('error', '', error || 'An error occurred while logging in', 'Close');
      })
    ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.RefreshTokenRequest),
      switchMap(() =>
        this.api.refreshToken().pipe(
          map((response) => AuthActions.RefreshTokenSuccess({ token: response.data.access_token })),
          catchError(error => of(AuthActions.RefreshTokenFailure({ error: error || 'An error occurred while refreshing token' })))
        )
      )
    )
  );

  refreshTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.RefreshTokenSuccess),
      map(({ token }) => {
        this.authStoreService.setToken(token);
      })
    ),
    { dispatch: false }
  );

  refreshTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.RefreshTokenFailure),
      map(({ error }) => {
        this.authStoreService.clear();
        // Optionally, you can also show a notification or redirect to login page here
      })
    ),
    { dispatch: false }
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ForgotPasswordRequest),
      switchMap(({ payload }) =>
        this.api.forgotPassword(payload).pipe(
          map(() => AuthActions.ForgotPasswordSuccess()),
          catchError(error => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
            }
            return of(AuthActions.ForgotPasswordFailure({ error: error || 'An error occurred while requesting password reset' }));
          })
        )
      )
    )
  );

  forgotPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ForgotPasswordSuccess),
      map(() => {
        this.snackbar.showNotification('success', '', 'Password reset link sent successfully! Please check your phone.', 'Close');
      })
    ),
    { dispatch: false }
  );

  forgotPasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ForgotPasswordFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while requesting password reset', 'Close');
      })
    ),
    { dispatch: false }
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ResetPasswordRequest),
      switchMap(({ payload }) =>
        this.api.forgotPasswordReset(payload).pipe(
          map(() => AuthActions.ResetPasswordSuccess()),
          catchError(error => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
            }
            return of(AuthActions.ResetPasswordFailure({ error: error || 'An error occurred while resetting password' }));
          })
        )
      )
    )
  );

  resetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ResetPasswordSuccess),
      map(() => {
        this.snackbar.showNotification('success', '', 'Password reset successfully! Please login to continue.', 'Close');
      })
    ),
    { dispatch: false }
  );

  resetPasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ResetPasswordFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while resetting password', 'Close');
      })
    ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LogoutRequest),
      switchMap(() =>
        this.api.logout().pipe(
          map(() => AuthActions.LogoutSuccess()),
          catchError(error => of(AuthActions.LogoutFailure({ error: error || 'An error occurred while logging out' })))
        )
      )
    )
  );

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LogoutSuccess),
      map(() => {
      })
    ),
    { dispatch: false }
  );

  logoutFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.LogoutFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while logging out', 'Close');
      })
    ),
    { dispatch: false }
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ChangePasswordRequest),
      switchMap(({ payload }) =>
        this.api.changePassword(payload).pipe(
          map(() => AuthActions.ChangePasswordSuccess()),
          catchError(error => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
            }
            return of(AuthActions.ChangePasswordFailure({ error: error || 'An error occurred while changing password' }));
          })
        )
      )
    )
  );

  changePasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ChangePasswordSuccess),
      map(() => {
        this.snackbar.showNotification('success', '', 'Password changed successfully!', 'Close');
      })
    ),
    { dispatch: false }
  );

  changePasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.ChangePasswordFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while changing password', 'Close');
      })
    ),
    { dispatch: false }
  );

  setPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.SetPasswordRequest),
      switchMap(({ payload }) =>
        this.api.setPassword(payload).pipe(
          map(() => AuthActions.SetPasswordSuccess()),
          catchError(({ error }) => {
            if (error.status === 422) {
              return of(AuthActions.AuthValidationError({ validationError: error.error.errors }));
            }
            return of(AuthActions.SetPasswordFailure({ error: error || 'An error occurred while setting password' }));
          })
        )
      )
    )
  );

  setPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.SetPasswordSuccess),
      map(() => {
        this.snackbar.showNotification('success', '', 'Password set successfully! Please login to continue.', 'Close');
      })
    ),
    { dispatch: false }
  );

  setPasswordFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.SetPasswordFailure),
      map(({ error }) => {
        this.snackbar.showNotification('error', '', error || 'An error occurred while setting password', 'Close');
      })
    ),
    { dispatch: false }
  );
}
